/**
 * Pricing Page Interactions
 * Handles animations, filtering, and interactive elements for the pricing page
 */

class PricingPage {
	constructor() {
		this.init();
	}

	init() {
		this.setupAnimations();
		this.setupFiltering();
		this.setupHoverEffects();
		this.setupScrollAnimations();
	}

	/**
	 * Setup scroll-triggered animations
	 */
	setupAnimations() {
		// Intersection Observer for fade-in animations
		const observerOptions = {
			threshold: 0.1,
			rootMargin: "0px 0px -50px 0px",
		};

		const observer = new IntersectionObserver((entries) => {
			entries.forEach((entry) => {
				if (entry.isIntersecting) {
					entry.target.classList.add("visible");
				}
			});
		}, observerOptions);

		// Observe all pricing table rows
		document.querySelectorAll(".pricing__table-row").forEach((row, index) => {
			row.classList.add("fade-in");
			row.style.transitionDelay = `${index * 0.1}s`;
			observer.observe(row);
		});

		// Observe header elements
		const header = document.querySelector(".pricing__header");
		if (header) {
			header.classList.add("fade-in");
			observer.observe(header);
		}
	}

	/**
	 * Setup category filtering functionality
	 */
	setupFiltering() {
		const filterButtons = document.querySelectorAll(".pricing__filter-btn");
		const pricingRows = document.querySelectorAll(".pricing__table-row");

		if (filterButtons.length === 0) return;

		filterButtons.forEach((button) => {
			button.addEventListener("click", (e) => {
				e.preventDefault();

				// Remove active class from all buttons
				filterButtons.forEach((btn) => btn.classList.remove("active"));

				// Add active class to clicked button
				button.classList.add("active");

				const filter = button.dataset.filter;

				// Filter rows
				pricingRows.forEach((row) => {
					if (filter === "all" || row.dataset.category === filter) {
						row.style.display = "grid";
						row.classList.add("fade-in");
					} else {
						row.style.display = "none";
						row.classList.remove("fade-in");
					}
				});
			});
		});
	}

	/**
	 * Setup hover effects for pricing table rows
	 */
	setupHoverEffects() {
		const rows = document.querySelectorAll(".pricing__table-row");

		rows.forEach((row) => {
			row.addEventListener("mouseenter", () => {
				row.style.transform = "translateX(5px)";
				row.style.boxShadow = "0 5px 20px rgba(0, 0, 0, 0.1)";
			});

			row.addEventListener("mouseleave", () => {
				row.style.transform = "translateX(0)";
				row.style.boxShadow = "none";
			});
		});
	}

	/**
	 * Setup scroll animations for service items
	 */
	setupScrollAnimations() {
		const serviceItems = document.querySelectorAll(".pricing__service");

		const serviceObserver = new IntersectionObserver(
			(entries) => {
				entries.forEach((entry, index) => {
					if (entry.isIntersecting) {
						setTimeout(() => {
							entry.target.style.opacity = "1";
							entry.target.style.transform = "translateX(0)";
						}, index * 100);
					}
				});
			},
			{ threshold: 0.1 }
		);

		serviceItems.forEach((item) => {
			item.style.opacity = "0";
			item.style.transform = "translateX(-20px)";
			item.style.transition = "all 0.3s ease";
			serviceObserver.observe(item);
		});
	}

	/**
	 * Animate price numbers
	 */
	animatePriceNumbers() {
		const priceElements = document.querySelectorAll(".pricing__service-price");

		priceElements.forEach((element) => {
			const finalPrice = element.textContent;
			const numericPrice = parseFloat(finalPrice.replace(/[^0-9.]/g, ""));

			if (isNaN(numericPrice)) return;

			let currentPrice = 0;
			const increment = numericPrice / 30;
			const timer = setInterval(() => {
				currentPrice += increment;
				if (currentPrice >= numericPrice) {
					element.textContent = finalPrice;
					clearInterval(timer);
				} else {
					element.textContent = "$" + Math.floor(currentPrice);
				}
			}, 50);
		});
	}

	/**
	 * Setup mobile responsive interactions
	 */
	setupMobileInteractions() {
		if (window.innerWidth <= 768) {
			// Add touch-friendly interactions for mobile
			const rows = document.querySelectorAll(".pricing__table-row");

			rows.forEach((row) => {
				row.addEventListener("touchstart", () => {
					row.style.transform = "scale(0.98)";
				});

				row.addEventListener("touchend", () => {
					row.style.transform = "scale(1)";
				});
			});
		}
	}

	/**
	 * Initialize pricing page
	 */
	static init() {
		if (document.querySelector(".pricing")) {
			new PricingPage();
		}
	}
}

// Initialize when DOM is ready
$(document).ready(() => {
	console.log("Pricing page initializing...");
	PricingPage.init();

	// Force show all table rows
	setTimeout(() => {
		document.querySelectorAll(".pricing__table-row").forEach((row) => {
			row.style.opacity = "1";
			row.style.transform = "translateY(0)";
			row.classList.add("visible");
		});
	}, 100);
});

// Re-initialize on window resize
$(window).on("resize", () => {
	if (document.querySelector(".pricing")) {
		new PricingPage();
	}
});

// Export for use in other modules
window.PricingPage = PricingPage;
